/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICoreContext, IGeneric, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
// @ts-ignore
import { getCartState } from '@msdyn365-commerce/global-state';

import getOrderStateAsync, { GetOrderStateInput } from '../actions/get-order-state.action';
import getOrderStatusAsync, { GetOrderStatusInput } from '../actions/get-order-status.action';
import getEcomCartAsync, { ITSGetEcomCartInput } from '../actions/get-ecom-cart.action';
import {
    IITSCustomerEcomCartsSearchCriteria,
    IITSGetAllCustomerEcomCartsResponse,
    IITSGetAllCustomerSuspendedCart,
    IITSIntegrationCartLine
} from '../custom-actions/DataServiceEntities.g';
import { lAC_ITSGetAllCustomerEcomCartsAsync } from '../custom-actions/DataActionExtension.g';
import {
    getAuthToken,
    getCartData,
    getCustomerId,
    getEventId,
    getNewCartPosData,
    getSelectedStoreId,
    getTableId,
    removeNewCartPosData,
    setCartData,
    setNewCartPosData
} from './cookieutils';
import { refreshToken } from './authService';
import { retireActiveCart } from './cartsession';
import { carryStoreOrderEndedAcrossReload, StoreOrderEndedMessages, StoreOrderEndedState } from './storeorderended';
import { applyLineDeliveryContext } from './linedeliverycontext';
import { deriveOrderChanges, hasOrderChanges, readHeldOrderHeader, readSentLines, recordSentLines } from './orderchanges';
import { getResumeCartId, hasOrderOpenForModify, makeCartWritable, recordPulledOrder, resumeOrderForModify } from './resumeorderformodify';
import { readCartCommitted } from './orderstate';
import { SESSION_EXPIRED_CODE } from './storerequestfailure';
import { classifyUnopenedOrder, UnopenedOrderReason } from './unopenedorder';

/**
 * opened - this channel now holds the order as the store has it.
 * gone - the store answered, and it no longer holds that order.
 * held - the store's copy was read, but the order could not be taken up for editing.
 * unavailable - the store could not be asked, so nothing was changed.
 * not-ours - the store refused to open the order for this caller, and said nothing about whose it is.
 */
export type StoreOrderRefreshStatus = 'opened' | 'gone' | 'held' | 'unavailable' | 'not-ours';

/** What the till has done with an order, as the store itself reports it. */
export type StoreOrderState = 'open' | 'completed' | 'voided';

export interface IStoreOrderRefresh {
    status: StoreOrderRefreshStatus;
    cart?: Cart;
    /** Why an order the store could not be read for was not opened, where it was not. */
    failure?: UnopenedOrderReason;
}

/**
 * Why the store turned a checkout away. Each of these comes back as a structured IsSuccess false response
 * rather than as a failure, and the store applies nothing before returning them.
 */
export type StoreCheckoutRefusal =
    | 'stale-cart-line'
    | 'order-held-at-pos'
    | 'item-unavailable'
    | 'order-already-placed'
    | 'payment-method-unavailable'
    | 'order-needs-reopening'
    | 'checkout-in-progress'
    | 'order-not-found'
    | 'venue-not-configured'
    | 'modifiers-invalid'
    | 'session-expired'
    | 'order-not-editable'
    | 'store-unreachable'
    | 'unknown';

/** What a line named by the store was waiting to do, before it was dropped. */
export type DroppedCartLineChange = 'removal' | 'edit' | 'none';

/**
 * The keys the order tracking id can arrive under. The store allocates one the first time it saves a
 * cart, so this names a cart the store has seen - not one that has been placed. Only the store's own
 * list of the orders it holds answers that.
 */
const ORDER_TRACKING_ID_STAMPS = ['OrderTrackingId', 'LS_OrderTrackingId', 'ORDERTRACKINGID'];

/** Properties a cart the store has saved carries. One a reader invented to answer a miss has none. */
const PLACED_ORDER_STAMPS = [...ORDER_TRACKING_ID_STAMPS, 'ITS_TableEventId'];

/**
 * The resource id the store raises when a read of an order finds nothing usable under that id. It is the
 * only refusal that speaks for the order itself - the store's other not-found ids name a channel or a
 * configuration, and say nothing about whether the order is still there.
 */
const STORE_HAS_NO_SUCH_ORDER_RESOURCE_ID = 'LSH_IntegrationCartNotFound';

/**
 * The store's refusal to open an order for a caller it does not belong to. It is raised rather than
 * returned, so it arrives as an error carrying this resource id and never as a response this channel can
 * read a code off. It also covers an order that belongs to nobody, which is why nothing said about it may
 * describe who holds it.
 */
const STORE_ORDER_NOT_OURS_RESOURCE_ID = 'LSH_EcomCartNotOwnedByCaller';

/** The name the store holds the order open for modification under, as the handshake recorded it, or undefined. */
export const getOpenOrderStoreId = (ecomCartId: string): string | undefined => {
    const handshake = getCartData();

    return handshake?.StoreCartId && handshake.CartId === ecomCartId ? String(handshake.StoreCartId) : undefined;
};

/**
 * Whether the store answered and said it has no such order, as opposed to not answering at all. Only a
 * refusal the store itself issued may take a customer's order away from them, so anything that cannot be
 * read as one - an expired token, a permission failure, a store having a bad minute - is reported as
 * unavailable and leaves the order alone.
 */
const readFailureResourceId = (error: unknown): string | undefined => {
    const actionError = error as {
        data?: { ErrorResourceId?: string };
        error?: { data?: { ErrorResourceId?: string } };
    };

    // A data action passes the proxy's error on inside one of its own, so the id sits a level down
    // whenever that wrapping happened.
    return actionError?.data?.ErrorResourceId ?? actionError?.error?.data?.ErrorResourceId;
};

/**
 * Whether the store refused to open this order for this caller. The refusal is thrown rather than
 * returned, so it reaches this channel as an error and not as a response with a code on it.
 */
const isOrderNotOursFailure = (error: unknown): boolean => readFailureResourceId(error) === STORE_ORDER_NOT_OURS_RESOURCE_ID;

const isOrderMissingFailure = (error: unknown): boolean => {
    const actionError = error as {
        data?: { ErrorMessage?: string; ErrorResourceId?: string };
        error?: { data?: { ErrorResourceId?: string } };
        message?: string;
    };

    // A named refusal arrives as the resource id it was raised under. A data action passes the proxy's
    // error on inside one of its own, so the id sits a level down whenever that wrapping happened.
    const resourceId = readFailureResourceId(error);
    if (resourceId === STORE_HAS_NO_SUCH_ORDER_RESOURCE_ID) {
        return true;
    }

    const reported = `${actionError?.data?.ErrorMessage ?? ''} ${actionError?.message ?? ''}`;

    // An answer given before the store named its refusals carries no identity of its own, and the status it
    // was read off the Carts endpoint with is the only part of it that reaches this far. A failure that never
    // reached the store carries no status at all, and must not be read as the store having spoken.
    const status = /Error (\d{3}):/.exec(reported)?.[1];
    if (status) {
        return status === '404' || status === '410';
    }

    return reported.includes('Cart JSON is null or empty');
};

/**
 * Reads this channel's copy of the cart back into the page. getCartState is instance cached, so until this
 * runs the page goes on showing the lines it held before the store's copy replaced them - and the store's
 * copy replaces them whatever the read then decides about the order.
 */
const readCartBack = async (context: ICoreContext<IGeneric<IAny>>, storeOrderId: string): Promise<void> => {
    try {
        const cartState = await getCartState(context.actionContext);
        await cartState.refreshCart({});
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the refreshed order was not read back', { storeOrderId, error });
    }
};

const readStamp = (cart: Cart | undefined, stampKeys: string[]): string | undefined =>
    cart?.ExtensionProperties?.find(property => stampKeys.includes(property.Key ?? '') && !!property.Value?.StringValue)?.Value
        ?.StringValue;

/**
 * Whether what came back is a cart the store never had. A reader that answers a missing cart with a fresh
 * empty one, rather than with a status, is indistinguishable from a voided order by its lines alone - but
 * a cart the store really holds still carries the stamps this channel put on it when the order was placed,
 * and a newly invented one carries none. That keeps an order a suite attendant has emptied line by line,
 * without voiding it, from being read as gone.
 */
const isUnknownToStore = (cart: Cart): boolean => {
    if (cart.CartLines?.length) {
        return false;
    }

    return !readStamp(cart, PLACED_ORDER_STAMPS);
};

/**
 * The name the store holds a local order under, out of the orders it has suspended for this customer. The
 * store mints that name itself, so nothing about it can be derived from this channel's cart id: the order
 * tracking id both sides carry is what they are matched on, and a name that still spells out this
 * channel's cart id is honoured for the orders placed while the store composed one.
 *
 * Undefined when no order the store named can be tied to this cart. An order picked by anything weaker
 * than identity would be an order the customer never opened, and the changes would be applied to it. A
 * cart that names its order tracking id is answered by that id alone, so a composed name belonging to
 * some other order is never read as this one.
 */
export const findStoreOrderIdForCart = (cart: Cart, suspendedOrders: IITSGetAllCustomerSuspendedCart[]): string | undefined => {
    const cartId = cart.Id;
    if (!cartId) {
        return undefined;
    }

    const orderTrackingId = readStamp(cart, ORDER_TRACKING_ID_STAMPS);
    if (orderTrackingId) {
        // The store does not allocate a tracking number per order - measured against the store's own
        // completed transactions, 234 orders carried 96 distinct numbers, one of them shared by nine.
        // Two orders answering to the same number cannot be told apart, and picking either would send
        // the customer's changes to an order they never opened.
        const named = suspendedOrders.filter(order => !!order.TransactionId && order.OrderTrackingId === orderTrackingId);

        return named.length === 1 ? named[0].TransactionId : undefined;
    }

    return suspendedOrders.find(order => order.TransactionId === cartId)?.TransactionId;
};

/** Whether the store's copy of an order has changed since this channel last read it. */
export type StoreOrderChange = 'changed' | 'unchanged' | 'no-baseline' | 'no-marker' | 'unknown';

/** The customer's orders at this table and event, asked fresh, with one retry on an expired token. */
const askForCustomerOrders = async (
    criteria: IITSCustomerEcomCartsSearchCriteria,
    customerId: string,
    context: ICoreContext<IGeneric<IAny>>
): Promise<IITSGetAllCustomerEcomCartsResponse> => {
    try {
        return await lAC_ITSGetAllCustomerEcomCartsAsync({ callerContext: context.actionContext }, getAuthToken(), customerId, criteria);
    } catch (error) {
        if (readFailureResourceId(error) !== '401') {
            throw error;
        }

        const refreshedToken = await refreshToken(context.actionContext);
        if (!refreshedToken) {
            throw error;
        }

        return lAC_ITSGetAllCustomerEcomCartsAsync({ callerContext: context.actionContext }, refreshedToken, customerId, criteria);
    }
};

/**
 * The store's row for the order this cart holds and the change marker on it. Undefined where the store gave no
 * row; a row without a marker is a store package that does not carry one.
 */
export const readStoreChangeMarker = async (
    cart: Cart,
    context: ICoreContext<IGeneric<IAny>>
): Promise<{ marker: string | undefined } | undefined> => {
    const channelId = parseInt(String(getCartData()?.ChannelId || getSelectedStoreId() || ''), 10);
    const customerId = getCustomerId() || '';

    if (!msdyn365Commerce.isBrowser || !cart.Id || !customerId || !Number.isFinite(channelId) || channelId <= 0) {
        return undefined;
    }

    const criteria: IITSCustomerEcomCartsSearchCriteria = {
        ChannelId: channelId,
        EventId: getEventId() || '',
        TableAssigned: getTableId() || '',
        Top: 1000,
        Skip: 0,
        ColumnName: 'ORDERTRACKINGID',
        IsDescending: true
    };

    try {
        const orders = (await askForCustomerOrders(criteria, customerId, context))?.ITSGetAllCustomerSuspendedCart ?? [];
        const storeOrderId = findStoreOrderIdForCart(cart, orders);
        const order = orders.find(candidate => !!storeOrderId && candidate.TransactionId === storeOrderId);

        return order ? { marker: order.ChangeVersion || undefined } : undefined;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the store did not give a change marker for this order', { cartId: cart.Id, error });

        return undefined;
    }
};

/**
 * The name the store holds this cart's order under: the one recorded when the order was pulled or opened, else
 * the one the store's own list ties to the cart, which is then recorded. Undefined where neither names it.
 */
export const resolveOpenOrderStoreId = async (cart: Cart, context: ICoreContext<IGeneric<IAny>>): Promise<string | undefined> => {
    const recorded = cart.Id ? getOpenOrderStoreId(cart.Id) : undefined;
    if (recorded || !cart.Id) {
        return recorded;
    }

    const channelId = parseInt(String(getCartData()?.ChannelId || getSelectedStoreId() || ''), 10);
    const customerId = getCustomerId() || '';
    if (!msdyn365Commerce.isBrowser || !customerId || !Number.isFinite(channelId) || channelId <= 0) {
        return undefined;
    }

    const criteria: IITSCustomerEcomCartsSearchCriteria = {
        ChannelId: channelId,
        EventId: getEventId() || '',
        TableAssigned: getTableId() || '',
        Top: 1000,
        Skip: 0,
        ColumnName: 'ORDERTRACKINGID',
        IsDescending: true
    };

    try {
        const orders = (await askForCustomerOrders(criteria, customerId, context))?.ITSGetAllCustomerSuspendedCart ?? [];
        const storeOrderId = findStoreOrderIdForCart(cart, orders);
        if (storeOrderId) {
            recordPulledOrder(cart.Id, storeOrderId);
        }

        return storeOrderId;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the store did not say which order this cart holds', { cartId: cart.Id, error });

        return undefined;
    }
};

/** What the store says has become of an order, from one call: over, open with its change marker, or no answer. */
export interface IOpenOrderStatus {
    answered: boolean;
    ended?: StoreOrderEndedState;
    marker?: string;
}

const ENDED_STATES = new Map<string, StoreOrderEndedState>([
    ['Voided', 'voided'],
    ['Completed', 'completed'],
    ['NotFound', 'gone']
]);

const OPEN_STATES = ['Suspended', 'OpenAtRegister'];

/** How long one answer serves every check made while a page loads. */
const STATUS_REUSE_MS = 5000;

const recentStatuses = new Map<string, { at: number; status: Promise<IOpenOrderStatus> }>();

/** Drops the answers being shared, which the guest's own change to the order has made stale. */
export const forgetOrderStatuses = (): void => {
    recentStatuses.clear();
};

const askOrderStatus = async (
    storeOrderId: string,
    channelId: number,
    context: ICoreContext<IGeneric<IAny>>
): Promise<IOpenOrderStatus> => {
    try {
        const answer = await getOrderStatusAsync(new GetOrderStatusInput(storeOrderId, channelId, getAuthToken()), context.actionContext);
        console.log('LTS Info', 'storeordersync: the store named the status of this order', { storeOrderId, state: answer?.State });

        const ended = ENDED_STATES.get(answer?.State ?? '');
        if (ended) {
            return { answered: true, ended };
        }

        return OPEN_STATES.includes(answer?.State ?? '')
            ? { answered: true, marker: answer?.ChangeVersion || undefined }
            : { answered: false };
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the store did not say what became of this order', { storeOrderId, error });

        return { answered: false };
    }
};

/**
 * What the store says has become of an order, in one call. The answers given while a page loads are shared,
 * so the checks it makes cost one call between them.
 * @param storeOrderId - The name the store holds the order under.
 * @param context - The page's context.
 * @param reuse - False to ask afresh, as a baseline taken after a change has to.
 * @returns The store's answer.
 */
export const readOpenOrderStatus = async (
    storeOrderId: string,
    context: ICoreContext<IGeneric<IAny>>,
    reuse: boolean = true
): Promise<IOpenOrderStatus> => {
    const channelId = parseInt(String(getCartData()?.ChannelId || getSelectedStoreId() || ''), 10);
    if (!msdyn365Commerce.isBrowser || !storeOrderId || !Number.isFinite(channelId) || channelId <= 0) {
        return { answered: false };
    }

    const key = `${channelId}|${storeOrderId}`;
    const recent = recentStatuses.get(key);
    if (reuse && recent && Date.now() - recent.at < STATUS_REUSE_MS) {
        return recent.status;
    }

    const status = askOrderStatus(storeOrderId, channelId, context);
    recentStatuses.set(key, { at: Date.now(), status });

    return status;
};

/** Keeps the store's current change marker on the handshake, as the version this channel's copy matches. */
export const recordStoreChangeBaseline = async (cart: Cart, context: ICoreContext<IGeneric<IAny>>): Promise<void> => {
    const storeOrderId = cart.Id ? getOpenOrderStoreId(cart.Id) : undefined;
    const marker = storeOrderId
        ? (await readOpenOrderStatus(storeOrderId, context, false)).marker
        : (await readStoreChangeMarker(cart, context))?.marker;
    const handshake = getCartData();

    if (!marker || !handshake || handshake.CartId !== cart.Id) {
        return;
    }

    setCartData({ ...handshake, ChangeVersion: marker, ChangeVersionRecordedAt: Date.now() });
};

/**
 * Whether the store's copy of the order changed since the baseline, without the destructive pull.
 * @param cart - This channel's copy of the order.
 * @param context - The page's context.
 * @returns Unknown wherever the store gave no answer, which is never read as changed; no-marker where its
 * package does not carry the marker.
 */
export const checkStoreOrderChange = async (cart: Cart, context: ICoreContext<IGeneric<IAny>>): Promise<StoreOrderChange> => {
    const handshake = getCartData();
    const baseline: string | undefined = handshake?.CartId === cart.Id ? handshake?.ChangeVersion : undefined;
    const storeOrderId = cart.Id ? getOpenOrderStoreId(cart.Id) : undefined;
    const status = storeOrderId ? await readOpenOrderStatus(storeOrderId, context) : undefined;
    const read = status
        ? status.answered && !status.ended
            ? { marker: status.marker }
            : undefined
        : await readStoreChangeMarker(cart, context);

    if (!read) {
        return 'unknown';
    }

    const current = read.marker;
    if (!current) {
        return 'no-marker';
    }

    if (!baseline) {
        return 'no-baseline';
    }

    return current === baseline ? 'unchanged' : 'changed';
};

/** What bringing the open order up to date on page entry did; unconfirmed is an empty order the store does not tie to any of its own. */
export interface IOrderEntrySync {
    outcome: 'no-open-order' | 'kept' | 'pulled' | 'changed-with-pending' | 'unconfirmed';
    change?: StoreOrderChange;
    refresh?: IStoreOrderRefresh;
}

/** The orders this browser session has already brought up to date. */
const STORE_ORDER_CHECKED_KEY = 'hs-store-order-checked';

/** A baseline older than this is treated as a guest returning to the order. */
const RETURNING_BASELINE_AGE_MS = 30 * 60 * 1000;

/** The answers that leave the store's copy possibly ahead of this one, so the pull runs as it always did. */
const CHANGES_THAT_PULL: StoreOrderChange[] = ['changed', 'no-baseline', 'no-marker'];

const entrySyncs = new Map<string, Promise<IOrderEntrySync>>();

const markOrderChecked = (openOrderId: string): void => {
    try {
        window.sessionStorage.setItem(STORE_ORDER_CHECKED_KEY, openOrderId);
    } catch (error) {
        console.log('LTS Info', 'storeordersync: could not note that this order was checked', error);
    }
};

/** Whether the guest is coming back to the open order, so its lines must wait for the store before showing. */
export const isReturningToOrder = (): boolean => {
    const openOrderId = getResumeCartId();
    if (!msdyn365Commerce.isBrowser || !openOrderId) {
        return false;
    }

    let checkedThisSession = false;
    try {
        checkedThisSession = window.sessionStorage.getItem(STORE_ORDER_CHECKED_KEY) === openOrderId;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: could not read whether this order was checked', error);
    }

    const handshake = getCartData();
    const recordedAt: number | undefined = handshake?.CartId === openOrderId ? handshake?.ChangeVersionRecordedAt : undefined;

    return !checkedThisSession || !recordedAt || Date.now() - recordedAt > RETURNING_BASELINE_AGE_MS;
};

const syncOpenOrder = async (openOrderId: string, context: ICoreContext<IGeneric<IAny>>): Promise<IOrderEntrySync> => {
    let cart: Cart | undefined;
    try {
        cart = (await getCartState(context.actionContext)).cart;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the cart could not be read, so the order is left as it is', error);

        return { outcome: 'kept', change: 'unknown' };
    }

    const change: StoreOrderChange = cart?.Id === openOrderId ? await checkStoreOrderChange(cart, context) : 'no-baseline';

    if (hasPendingOrderChanges(cart)) {
        return { outcome: change === 'changed' ? 'changed-with-pending' : 'kept', change };
    }

    if (change === 'unknown' && cart?.Id === openOrderId && !cart.CartLines?.length) {
        return { outcome: 'unconfirmed', change };
    }

    if (!CHANGES_THAT_PULL.includes(change)) {
        return { outcome: 'kept', change };
    }

    const storeOrderId = cart ? await resolveOpenOrderStoreId(cart, context) : undefined;
    const refresh = await refreshOrderFromStore(storeOrderId ?? '', context);

    return { outcome: 'pulled', change, refresh };
};

/**
 * Brings the order open for modification up to date once per page load, shared by every module that asks.
 * Pulls only when the store's copy may be ahead, and never over changes the guest has not sent.
 * @param context - The page's context.
 * @returns What was done, the same answer for every caller on the page.
 */
export const syncOpenOrderOnEntry = async (context: ICoreContext<IGeneric<IAny>>): Promise<IOrderEntrySync> => {
    const openOrderId = getResumeCartId() || '';
    if (!msdyn365Commerce.isBrowser || !openOrderId || !hasOrderOpenForModify(getSelectedStoreId() || '')) {
        return { outcome: 'no-open-order' };
    }

    const running =
        entrySyncs.get(openOrderId) ??
        syncOpenOrder(openOrderId, context).then(sync => {
            markOrderChecked(openOrderId);

            return sync;
        });
    entrySyncs.set(openOrderId, running);

    return running;
};

/** Whether the sync left no store order behind the one open for modification, so the suite's own order is still to be asked for. */
export const leavesOpenOrderUnconfirmed = (sync: IOrderEntrySync | undefined): boolean =>
    sync?.outcome === 'unconfirmed' || (sync?.outcome === 'pulled' && sync.refresh?.status === 'unavailable' && !sync.refresh.failure);

/** Whether the customer has changes on this order that have not reached the store yet. */
export const hasPendingOrderChanges = (cart?: Cart): boolean => {
    const sentLines = readSentLines(cart?.Id);
    if (cart && sentLines) {
        return hasOrderChanges(deriveOrderChanges(cart, sentLines, [], readHeldOrderHeader(cart.Id)));
    }

    const pendingChanges = getNewCartPosData();
    if (!pendingChanges) {
        return false;
    }

    return [pendingChanges.AddCartLines, pendingChanges.UpdateCartLines, pendingChanges.RemoveCartLines].some(
        lines => Array.isArray(lines) && lines.length > 0
    );
};

/**
 * Whether to open this channel's own copy of an order the store did not hand over.
 *
 * Only where the store said nothing. A store that could not be reached has given no answer about the
 * order, so the copy already held here is still the best this channel has, and opening it is where the
 * confirmation page started before it read the store at all.
 *
 * A store that answered and said it has no such order has given one, and opening a copy of it would put an
 * order the store does not have back in front of the customer as a live one they can go on editing. That
 * is the same answer for a refusal the store named and for one an older store gave as a status: both are
 * the store speaking.
 * @param status - What reading the store came to.
 * @returns True when this channel's own copy may still be opened.
 */
export const mayOpenOwnCopyOfOrder = (status: StoreOrderRefreshStatus): boolean => status === 'unavailable';

/**
 * What the store has done with an order, asked of the order itself rather than inferred from its absence
 * from the list of carts the store still holds. That absence conflates completed, voided, never suspended
 * and a lookup that failed, so it may not be acted on; this may.
 *
 * Answers for the one id it is asked with.
 *
 * The store cannot tell an id it does not recognise from an order it does not have, so a caller that asks
 * with the wrong one is answered exactly as though the order had never existed.
 *
 * Answers undefined when the question could not be put, which is not the same as an order the store has
 * finished with and must leave the order alone.
 * @param ecomCartId - This channel's own cart id.
 * @param channelId - The store channel to ask.
 * @param context - The page's context.
 * @returns What the store has done with the order, or undefined where it did not say.
 */
export const readStoreOrderState = async (
    ecomCartId: string,
    channelId: number | undefined,
    context: ICoreContext<IGeneric<IAny>>
): Promise<StoreOrderState | undefined> => {
    if (!ecomCartId || !channelId || !msdyn365Commerce.isBrowser) {
        return undefined;
    }

    try {
        const state = await getOrderStateAsync(new GetOrderStateInput(ecomCartId, channelId, getAuthToken()), context.actionContext);

        console.log('LTS Info', 'storeordersync: the store named the state of this order', {
            ecomCartId,
            state: state?.State,
            entryStatus: state?.EntryStatus
        });

        switch (state?.State) {
            case 'completed':
                return 'completed';
            case 'voided':
                return 'voided';
            case 'open':
                return 'open';
            default:
                return undefined;
        }
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the store did not say what became of this order', { ecomCartId, error });

        return undefined;
    }
};

/** How the store says this cart's order ended, or undefined while it is open, unanswered, or no order is recorded. */
export const readEndedOrderForCart = async (
    ecomCartId: string,
    context: ICoreContext<IGeneric<IAny>>
): Promise<StoreOrderEndedState | undefined> => {
    const storeOrderId = getOpenOrderStoreId(ecomCartId);

    return storeOrderId ? (await readOpenOrderStatus(storeOrderId, context)).ended : undefined;
};

/**
 * Takes the guest off a cart whose order the till has already finished with.
 *
 * Voiding or completing an order at the till settles the store's copy and leaves this channel's cart exactly
 * as it was, and the cart scope registry points the site back at it on the next load - so a voided order
 * comes back, and the guest can keep adding to something that will never be served.
 *
 * Only an answer the store gave about this order may take a basket away: completed, voided, or no order held.
 *
 * The caller reloads the page when a cart is retired; the message is carried across that reload.
 */
export const retireOrderTheStoreHasFinished = async (
    context: ICoreContext<IGeneric<IAny>>,
    authoredMessages?: StoreOrderEndedMessages
): Promise<StoreOrderEndedState | undefined> => {
    if (!msdyn365Commerce.isBrowser) {
        return undefined;
    }

    let cart: Cart | undefined;

    try {
        cart = (await getCartState(context.actionContext)).cart;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the cart could not be read, so nothing is retired', error);

        return undefined;
    }

    if (!cart?.Id || !cart.CartLines?.length) {
        return undefined;
    }

    const state = await readEndedOrderForCart(cart.Id, context);

    if (!state) {
        return undefined;
    }

    console.log('LTS Info', 'storeordersync: the till has finished with this order, taking the guest off it', {
        cartId: cart.Id,
        lines: cart.CartLines?.length,
        state
    });

    const newCartId = await retireActiveCart(context);

    if (!newCartId) {
        return undefined;
    }

    carryStoreOrderEndedAcrossReload(state, authoredMessages);

    return state;
};

/**
 * Brings the store channel's copy of an order into this one and opens it for modification, so what the
 * customer sees and the baseline their changes are measured against are both what the store holds now.
 *
 * The order id must be the one the store uses. Reading the store replaces this channel's copy of the order
 * wholesale and clears the changes waiting on it, so callers that may hold unsent changes have to check
 * hasPendingOrderChanges immediately before calling.
 */
export const refreshOrderFromStore = async (storeOrderId: string, context: ICoreContext<IGeneric<IAny>>): Promise<IStoreOrderRefresh> => {
    const channelId = parseInt(getSelectedStoreId() || '', 10);

    if (!storeOrderId || !msdyn365Commerce.isBrowser || !Number.isFinite(channelId) || channelId <= 0) {
        return { status: 'unavailable' };
    }

    let storeCart: Cart | undefined;

    try {
        const ecomCartId = (await getCartState(context.actionContext)).cart?.Id || '';
        storeCart = await getEcomCartAsync(
            new ITSGetEcomCartInput(storeOrderId, channelId, getAuthToken(), getCustomerId() || '', ecomCartId),
            context.actionContext
        );
    } catch (error) {
        if (isOrderNotOursFailure(error)) {
            console.log('LTS Info', 'storeordersync: the store refused to open this order for this caller', { storeOrderId });

            return { status: 'not-ours' };
        }

        const isMissing = isOrderMissingFailure(error);
        console.log('LTS Info', 'storeordersync: the store did not hand back the order', { storeOrderId, isMissing, error });

        return isMissing ? { status: 'gone' } : { status: 'unavailable', failure: classifyUnopenedOrder(error) };
    }

    if (!storeCart?.Id) {
        console.log('LTS Info', 'storeordersync: the store returned no order to open', { storeOrderId });
        return { status: 'unavailable', failure: 'temporary' };
    }

    if (isUnknownToStore(storeCart)) {
        console.log('LTS Info', 'storeordersync: the store has nothing under this order id', { storeOrderId });
        await makeCartWritable(storeCart.Id, context);
        await readCartBack(context, storeOrderId);

        return { status: 'gone' };
    }

    const openedCart = await resumeOrderForModify(storeCart.Id, storeOrderId, context);
    if (!openedCart) {
        recordPulledOrder(storeCart.Id, storeOrderId);
        await makeCartWritable(storeCart.Id, context);
        await readCartBack(context, storeOrderId);
        await applyLineDeliveryContext(context);
        await recordStoreChangeBaseline(storeCart, context);
        recordSentLines(storeCart);

        return { status: 'held', cart: storeCart };
    }

    await readCartBack(context, storeOrderId);
    // The store's lines arrive without a delivery mode, and the channel taxes a line only once it has one.
    await applyLineDeliveryContext(context);
    await recordStoreChangeBaseline(openedCart, context);

    return { status: 'opened', cart: openedCart };
};

/** Whether an update the store did not confirm may still have reached its order: anything but a store that answered unchanged. */
export const mayHaveTakenUnconfirmedUpdate = (change: StoreOrderChange): boolean => change !== 'unchanged' && change !== 'unknown';

/**
 * Replaces this channel's copy with the store's after an update the store did not confirm, where the store's
 * order has moved since it was read, so the changes it may already hold are not sent to it a second time.
 * @param cart - This channel's copy of the order.
 * @param storeOrderId - The order id the store uses.
 * @param context - The page's context.
 * @returns True when the store's copy was pulled.
 */
export const pullAfterUnconfirmedUpdate = async (
    cart: Cart,
    storeOrderId: string,
    context: ICoreContext<IGeneric<IAny>>
): Promise<boolean> => {
    const change = await checkStoreOrderChange(cart, context);
    if (!mayHaveTakenUnconfirmedUpdate(change)) {
        return false;
    }

    console.log('LTS Info', 'storeordersync: the store moved after an update it did not confirm, so its copy replaces this one', {
        storeOrderId,
        change
    });
    removeNewCartPosData();
    await refreshOrderFromStore(storeOrderId, context);

    return true;
};

/**
 * Whether the guest is building an order of their own: lines on a cart the store has never handed over. Pulling the suite's
 * order would replace them, so they are left for the checkout to merge into it. A cart the store marked as its order's is
 * never one, even where the record of what the store holds was lost.
 * @param context - The page's context.
 * @returns True when the cart holds lines no store order records.
 */
export const holdsOwnUnsentOrder = async (context: ICoreContext<IGeneric<IAny>>): Promise<boolean> => {
    const cart = (await getCartState(context.actionContext)).cart;

    return !!cart?.CartLines?.some(line => !line.IsVoided) && !readSentLines(cart.Id) && readCartCommitted(cart) !== 'yes';
};

/** What looking for the suite's order on entry found: skipped where an order is already open here, the guest's cart holds lines of its own, or the browser lacks the scope. */
export interface ISuiteOrderOnEntry {
    outcome: 'skipped' | 'none' | 'opened' | 'not-opened';
    storeOrderId?: string;
    reason?: UnopenedOrderReason;
}

/**
 * Opens the suite's newest order at this table and event, as the menu does, for a page the guest may reach before the menu.
 * @param context - The page's context.
 * @returns What was found, and why an order that was found could not be opened.
 */
export const openSuiteOrderOnEntry = async (context: ICoreContext<IGeneric<IAny>>): Promise<ISuiteOrderOnEntry> => {
    const storeId = getSelectedStoreId() || '';
    const tableId = getTableId() || '';
    const customerId = getCustomerId() || '';
    const channelId = parseInt(storeId, 10);

    if (!msdyn365Commerce.isBrowser || !tableId || !customerId || !Number.isFinite(channelId) || hasOrderOpenForModify(storeId)) {
        return { outcome: 'skipped' };
    }

    if (await holdsOwnUnsentOrder(context)) {
        return { outcome: 'skipped' };
    }

    let storeOrderId: string | undefined;
    try {
        const criteria: IITSCustomerEcomCartsSearchCriteria = {
            ChannelId: channelId,
            EventId: getEventId() || '',
            TableAssigned: tableId,
            Top: 1,
            Skip: 0,
            ColumnName: 'ORDERTRACKINGID',
            IsDescending: true
        };
        storeOrderId = (await askForCustomerOrders(criteria, customerId, context))?.ITSGetAllCustomerSuspendedCart?.[0]?.TransactionId;
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the suite could not be asked for its order', error);

        return { outcome: 'not-opened', storeOrderId: '', reason: 'temporary' };
    }

    if (!storeOrderId) {
        return { outcome: 'none' };
    }

    try {
        const refresh = await refreshOrderFromStore(storeOrderId, context);
        if (refresh.status === 'unavailable' || refresh.status === 'not-ours') {
            return {
                outcome: 'not-opened',
                storeOrderId,
                reason: refresh.status === 'not-ours' ? 'refused' : refresh.failure ?? 'temporary'
            };
        }

        if (refresh.status === 'gone') {
            return { outcome: 'none' };
        }
    } catch (error) {
        console.log('LTS Info', 'storeordersync: the suite order could not be opened', { storeOrderId, error });

        return { outcome: 'not-opened', storeOrderId, reason: 'temporary' };
    }

    return { outcome: 'opened', storeOrderId };
};

/**
 * The store's own name for each refusal. A code is the store speaking about itself, so it is read before
 * the prose is, and a store that names one has already decided what the refusal is.
 */
const REFUSAL_CODES = new Map<string, StoreCheckoutRefusal>([
    ['LSH_IntegrationCartNotFound', 'order-not-found'],
    ['LSH_IntegrationCheckoutAlreadyInProgress', 'checkout-in-progress'],
    ['LSH_IntegrationCheckoutInProgress', 'checkout-in-progress'],
    ['LSH_IntegrationCartIdAlreadyUsed', 'order-already-placed'],
    ['LSH_IntegrationCartAlreadyExists', 'order-already-placed'],
    ['ErrorId_PaymentMethodNotFound', 'payment-method-unavailable'],
    ['ErrorId_PaymentMethodRequired', 'payment-method-unavailable'],
    ['ErrorId_ExcludedFromEcom', 'item-unavailable'],
    ['LSH_IntegrationOrderAlreadyPlaced', 'order-needs-reopening'],
    ['LSH_IntegrationStoreCartIdRequired', 'order-needs-reopening'],
    ['LSH_IntegrationCartResumedAtPos', 'order-held-at-pos'],
    ['LSH_CartLockedStatusCheckFailed', 'order-held-at-pos'],
    ['LSH_IntegrationCartLineNotFound', 'stale-cart-line'],
    ['LSH_IntegrationItemUnavailable', 'item-unavailable'],
    ['LSH_IntegrationVariantUnavailable', 'item-unavailable'],
    ['LSH_IntegrationChannelNotConfigured', 'venue-not-configured'],
    ['LSH_IntegrationModifiersInvalid', 'modifiers-invalid'],
    ['LSH_UnknownError', 'unknown'],
    ['LSH_IntegrationStoreCallFailed', 'store-unreachable'],
    ['LTS_CartNotFound', 'order-not-found'],
    ['LTS_IntegrationChannelNotFound', 'venue-not-configured'],
    ['LTS_ModsValidationFailed', 'modifiers-invalid'],
    ['ProductNotAvailableError', 'item-unavailable'],
    ['Microsoft_Dynamics_Commerce_Runtime_CartNotActive', 'order-not-editable'],
    [SESSION_EXPIRED_CODE, 'session-expired']
]);

/**
 * What a customer is told for each refusal. The store's own wording is never rendered: the ids it raises
 * today carry operator text, unfilled placeholders and instructions to open a POS screen, so the code is
 * read and the sentence is ours. What the store said is logged instead.
 */
const UNKNOWN_REFUSAL_MESSAGE = 'We could not send your order to the venue. Please try again.';

const REFUSAL_MESSAGES = new Map<StoreCheckoutRefusal, string>([
    ['stale-cart-line', 'This order has changed since you last saw it. Please refresh and try again.'],
    ['order-held-at-pos', 'This order is being worked on at the venue. Please try again shortly.'],
    ['item-unavailable', 'Something on this order is no longer available. Please review it and try again.'],
    ['order-already-placed', 'This order has already been placed. Open it again from your order history to change it.'],
    ['payment-method-unavailable', 'The selected card is not available on this account. Please choose a card again.'],
    ['order-needs-reopening', 'This order has already been placed. Open it again from your order history to change it.'],
    ['checkout-in-progress', 'This order is already being placed. Please wait a moment and check your orders before trying again.'],
    ['order-not-found', 'We could not find your order. Please start a new one.'],
    ['venue-not-configured', 'We cannot send your order to this venue right now. Please contact the venue.'],
    ['modifiers-invalid', 'Some choices on this order need reviewing before it can be sent.'],
    ['session-expired', 'Please sign in again to send this order.'],
    ['order-not-editable', 'This order is not open for changes. Please open it again from your order history.'],
    ['store-unreachable', 'We could not reach the venue just now. Your order is unchanged - please try again.'],
    ['unknown', UNKNOWN_REFUSAL_MESSAGE]
]);

export const describeStoreCheckoutRefusal = (refusal: StoreCheckoutRefusal): string =>
    REFUSAL_MESSAGES.get(refusal) ?? UNKNOWN_REFUSAL_MESSAGE;

export const classifyStoreCheckoutRefusal = (errorMessage?: string, errorCode?: string): StoreCheckoutRefusal => {
    const named = errorCode ? REFUSAL_CODES.get(errorCode) : undefined;
    if (named) {
        return named;
    }

    const message = errorMessage ?? '';

    if (/Line Id:.*(doesnt|doesn't) exist on the cart/i.test(message)) {
        return 'stale-cart-line';
    }

    if (/resumed on POS/i.test(message)) {
        return 'order-held-at-pos';
    }

    if (/not available on retail store/i.test(message)) {
        return 'item-unavailable';
    }

    if (/already exists on the POS/i.test(message)) {
        return 'order-already-placed';
    }

    return 'unknown';
};

/** The line the store named as no longer being on the order. */
export const readRefusedCartLineId = (errorMessage?: string): string | undefined => {
    const lineId = /Line Id:\s*(\S+?)\s+(?:doesnt|doesn't) exist on the cart/i.exec(errorMessage ?? '')?.[1];
    return lineId || undefined;
};

/**
 * Drops a line the store no longer has from the changes waiting to be sent, and reports what that line was
 * waiting to do. A removal the store has already carried out leaves the customer's intent satisfied, so the
 * rest of their changes can go on; an edit to a line that has gone cannot be carried out at all.
 */
export const dropPendingChangesForCartLine = (lineId: string): DroppedCartLineChange => {
    if (!lineId || !msdyn365Commerce.isBrowser) {
        return 'none';
    }

    const pendingChanges = getNewCartPosData();
    if (!pendingChanges) {
        return 'none';
    }

    const namesLine = (lines: unknown): boolean =>
        Array.isArray(lines) && (lines as IITSIntegrationCartLine[]).some(line => line.LineId === lineId);

    const withoutLine = (lines: unknown): IITSIntegrationCartLine[] =>
        (Array.isArray(lines) ? (lines as IITSIntegrationCartLine[]) : []).filter(line => line.LineId !== lineId);

    const wasRemoval = namesLine(pendingChanges.RemoveCartLines);
    const wasEdit = namesLine(pendingChanges.UpdateCartLines);

    if (!wasRemoval && !wasEdit) {
        return 'none';
    }

    setNewCartPosData({
        ...pendingChanges,
        UpdateCartLines: withoutLine(pendingChanges.UpdateCartLines),
        RemoveCartLines: withoutLine(pendingChanges.RemoveCartLines)
    });

    return wasRemoval ? 'removal' : 'edit';
};
